import { ChangeDetectionStrategy, Component, inject, model, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ComplexArticleBlueprintFactory } from '@admintool/article/generate-article/complex-article-blueprints/services/complex-article-blueprint.factory';
import { ComplexArticleRuleFromUsagePipe } from '@admintool/article/generate-article/complex-article-blueprints/complex-article-rule-from-usage.pipe';
import { ComplexArticleBlueprintDTO, ComplexArticleRuleParamTypeName } from '@admintool/article/generate-article/complex-article-blueprints/complex-article-blueprint';
import { PropertyValuePipe } from '@article/service/property-value.pipe';
import { PropertyUtils } from '@article/util/property-utils';
import { Log } from '@shared/debug/log';
import { SvgIconComponent } from '@shared/svg-icon/svg-icon.component';
import { ArrayUtils } from '@shared/util/array-utils';
import { assertExhaustiveSwitch } from '@shared/util/ts-utils';
import { BsDropdownDirective, BsDropdownMenuDirective, BsDropdownToggleDirective } from 'ngx-bootstrap/dropdown';

@Component({
  selector: 'app-complex-article-blueprint-rules',
  imports: [
    ComplexArticleRuleFromUsagePipe,
    FormsModule,
    SvgIconComponent,
    TranslateModule,
    BsDropdownDirective,
    BsDropdownToggleDirective,
    BsDropdownMenuDirective,
    PropertyValuePipe,
  ],
  templateUrl: './complex-article-blueprint-rules.component.html',
  styleUrl: './complex-article-blueprint-rules.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ComplexArticleBlueprintRulesComponent {
  private readonly complexArticleBlueprintRuleFactory = inject(ComplexArticleBlueprintFactory);

  public blueprint = model<ComplexArticleBlueprintDTO>();
  public update = output();

  public allRules = this.complexArticleBlueprintRuleFactory.allRules;

  public moveRule(index: number, offset: number): void {
    this.blueprint.update((usage) => {
      if(!usage) return undefined;
      ArrayUtils.moveItem(usage.rules, index, index + offset);
      return structuredClone(usage);
    });
  }

  public deleteRule(index: number): void {
    this.blueprint.update((usage) => {
      if(!usage) return undefined;
      usage.rules.splice(index, 1);
      return structuredClone(usage);
    });
    this.update.emit();
  }

  public addRule(ruleName: string): void {
    this.blueprint.update((usage) => {
      if(!usage) return undefined;
      const rule = this.complexArticleBlueprintRuleFactory.findRule(ruleName);
      if(Log.assertExists(rule, 'rule')) {
        usage.rules.push(rule.createUsage());
      }
      return structuredClone(usage);
    });
    this.update.emit();
  }

  public updateRule(index: number, newRuleEvent: Event): void {
    this.blueprint.update((blueprint) => {
      if(!blueprint) return undefined;
      const newRuleName = (newRuleEvent.target as HTMLSelectElement).value;
      const complexArticleRule = this.complexArticleBlueprintRuleFactory.findRule(newRuleName);
      if (!complexArticleRule) return undefined;
      blueprint.rules[index] = complexArticleRule.createUsage();
      return structuredClone(blueprint);
    })
  }

  public setParamValue(ruleIndex: number, paramName: string, type: ComplexArticleRuleParamTypeName, event: Event): void {
    const target = event.target as HTMLInputElement;
    this.blueprint.update((usage) => {
      if(!usage) return undefined;
      const params = usage.rules[ruleIndex].params;
      switch (type) {
        case 'STRING':
          PropertyUtils.setStringPropertyValue(params, paramName, target.value);
          break;
        case 'INT':
          PropertyUtils.setIntPropertyValue(params, paramName, Number.parseInt(target.value, 10));
          break;
        case 'FLOAT':
          PropertyUtils.setFloatPropertyValue(params, paramName, Number.parseFloat(target.value));
          break;
        case 'BOOLEAN':
          PropertyUtils.setBooleanPropertyValue(params, paramName, target.checked);
          break;
        default:
          assertExhaustiveSwitch(type);
      }
      return structuredClone(usage);
    });
  }

  public updateArrayParam(ruleIndex: number, paramName: string, paramType: ComplexArticleRuleParamTypeName, event: Event): void {
    const target = event.target as HTMLInputElement;
    this.blueprint.update((usage) => {
      if (!usage) return undefined;
      const params = usage.rules[ruleIndex].params;
      switch (paramType) {
        case 'STRING': {
          const stringResult = ArrayUtils.stringArrayFromString(target.value, ';');
          PropertyUtils.setStringPropertyValues(params, paramName, stringResult);
          target.value = stringResult.join(';');
          break;
        }
        case 'INT': {
          const intResult = ArrayUtils.intArrayFromString(target.value, ';');
          PropertyUtils.setIntPropertyValues(params, paramName, intResult);
          target.value = intResult.join(';');
          break;
        }
        case 'FLOAT': {
          const floatResult = ArrayUtils.numberArrayFromString(target.value, ';');
          PropertyUtils.setFloatPropertyValue(params, paramName, floatResult[0] ?? 0);
          target.value = floatResult.join(';');
          break;
        }
        case 'BOOLEAN': {
          throw new Error('boolean arrays are not supported');
        }
        default: {
          assertExhaustiveSwitch(paramType);
        }
      }
      return structuredClone(usage);
    })
  }

  public readonly Object = Object;
}
